import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { COLORS, SHADOWS, RADII, FONTS } from "app/views/dashboard/shared/tokens";
import { getEmployeeBasicInfo } from "app/services/Services";
import BasicInfo from "./employee-details/BasicInfo";
import AddressContact from "./employee-details/AddressContact";
import BankDetails from "./employee-details/BankDetails";
import SkillsDetails from "./employee-details/SkillsDetails";
import EducationDetails from "./employee-details/EducationDetails";
import DocumentsTab from "./employee-details/DocumentsTab";
import ExperienceDetails from "./employee-details/ExperienceDetails";

const T = {
  bg: COLORS.background,
  surface: COLORS.surface,
  cardBorder: COLORS.cardBorder,
  hairline: COLORS.hairline,
  gradient: COLORS.gradient,
  ink: COLORS.ink,
  body: COLORS.body,
  body2: COLORS.body2,
  muted: COLORS.muted,
  shadow: SHADOWS.card,
  tabActive: SHADOWS.tabActive,
  cardR: RADII.card,
  heading: FONTS.heading,
  body_f: FONTS.body,
};

const TABS = [
  { label: "Basic Info", Component: BasicInfo },
  { label: "Address & Contact", Component: AddressContact },
  { label: "Bank Details", Component: BankDetails },
  { label: "Skills", Component: SkillsDetails },
  { label: "Experience", Component: ExperienceDetails },
  { label: "Education", Component: EducationDetails },
  { label: "Documents", Component: DocumentsTab },
];

function ChevronRight() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#c1b8d1" strokeWidth="2.4" strokeLinecap="round">
      <path d="M10 6l6 6-6 6" />
    </svg>
  );
}

export default function EmployeeDetailsTab() {
  const [value, setValue] = useState(0);
  const navigate = useNavigate();
  const { id } = useParams();
  const ActiveComponent = TABS[value].Component;

  // Header used to just print the raw route UUID as "Employee ID: TT-<uuid>" --
  // fetch the actual employee record so it shows the real name + emp_id
  // (e.g. "Shital Zile · Employee ID: TT-2014") instead.
  const [headerInfo, setHeaderInfo] = useState(null);

  useEffect(() => {
    let active = true;
    getEmployeeBasicInfo(id)
      .then((res) => {
        if (active && res?.status === 200) {
          setHeaderInfo(res.data?.data || null);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [id]);

  const employeeFullName = [headerInfo?.first_name, headerInfo?.middle_name, headerInfo?.last_name]
    .filter(Boolean)
    .join(" ");

  return (
    <Box sx={{ background: T.bg, minHeight: "100vh" }}>
      <Box sx={{ padding: "26px 30px 44px", fontFamily: T.body_f, color: T.ink, maxWidth: 1100, margin: "0 auto", "@media (max-width:640px)": { padding: "20px 16px" } }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "20px", flexWrap: "wrap", mb: "20px" }}>
          <div>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: T.muted, mb: "7px" }}>
              <span onClick={() => navigate("/employees/list")} style={{ cursor: "pointer" }}>Employee Management</span>
              <ChevronRight />
              <span onClick={() => navigate("/employees/list")} style={{ cursor: "pointer" }}>Employees</span>
              <ChevronRight />
              <span style={{ color: T.body, fontWeight: 500 }}>Details</span>
            </Box>
            <h1 style={{ fontFamily: T.heading, fontSize: 26, margin: "0 0 5px", fontWeight: 600, letterSpacing: "-0.015em", color: T.ink }}>
              {employeeFullName || "Employee details"}
            </h1>
            <p style={{ margin: 0, fontSize: "14px", color: T.body2 }}>
              Employee ID: {headerInfo?.emp_id ? `TT-${headerInfo.emp_id}` : "—"}
            </p>
          </div>
          <button
            onClick={() => navigate("/employees/list")}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              fontSize: "13.5px", fontWeight: 500, color: T.body,
              background: T.surface, border: `1px solid ${T.cardBorder}`,
              padding: "10px 15px", borderRadius: "10px", cursor: "pointer",
              fontFamily: T.body_f,
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M14 6l-6 6 6 6" /></svg>
            Back to roster
          </button>
        </Box>

        {/* Tabs */}
        <Box
          role="tablist"
          aria-label="Employee detail sections"
          sx={{
            display: "flex",
            gap: "4px",
            background: T.surface,
            border: `1px solid ${T.cardBorder}`,
            borderRadius: "12px",
            padding: "4px",
            mb: "18px",
            overflowX: "auto",
            "&::-webkit-scrollbar": { height: 0 },
          }}
        >
          {TABS.map((tab, i) => (
            <Box
              key={tab.label}
              component="button"
              type="button"
              role="tab"
              aria-selected={value === i}
              onClick={() => setValue(i)}
              sx={{
                border: "none",
                whiteSpace: "nowrap",
                flexShrink: 0,
                fontFamily: T.body_f,
                fontSize: "13px",
                fontWeight: value === i ? 600 : 500,
                padding: "9px 15px",
                borderRadius: "9px",
                cursor: "pointer",
                background: value === i ? T.gradient : "transparent",
                color: value === i ? "#fff" : T.body2,
                boxShadow: value === i ? T.tabActive : "none",
                transition: "all 0.15s",
              }}
            >
              {tab.label}
            </Box>
          ))}
        </Box>

        {/* Active panel */}
        <Box role="tabpanel">
          <ActiveComponent />
        </Box>
      </Box>
    </Box>
  );
}
